import type { inferRouterOutputs } from "@trpc/server";
import type { ElementRouter } from "replo-publisher/src/trpc/routers/element";

import * as React from "react";

import { keepPreviousData, skipToken } from "@tanstack/react-query";
import { setCanvasHtml } from "@v1/components/canvas/canvas-reducer";
import {
  setActiveCurrency,
  setActiveLanguage,
  setMoneyFormats,
  setOkendoWidgetNamespace,
  setShopifyUrlRoot,
} from "@v1/reducers/commerce-reducer";
import {
  selectDraftElementId,
  selectDraftElementType,
  selectStoreUrl,
  setSrcDocFonts,
  setSrcDocFontUrls,
  setSrcDocRootFont,
} from "@v1/reducers/core-reducer";
import { replaceCdnUrls } from "@v1/reducers/utils/api-reducer-utils";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { modalStore } from "@v2/store/modalStore";
import { elementRouter } from "@v2/trpc/routes/element";
import { exhaustiveSwitch, isNullish } from "replo-utils/lib/misc";

export default function useAutoRefetchCanvasDocument() {
  const dispatch = useEditorDispatch();
  const projectId = useCurrentProjectId();
  const draftElementId = useEditorSelector(selectDraftElementId);
  const storeUrl = useEditorSelector(selectStoreUrl);
  const draftElementType = useEditorSelector(selectDraftElementType);

  // NOTE (Ben O., 2026-02-17, USE-2357): Track whether we've already fetched
  // for the current projectId+elementId+elementType combination. We use a ref
  // instead of usePrevious to avoid a race condition where the query's data
  // becomes undefined on re-renders: the old shouldSkip logic used usePrevious
  // to compare prev vs current values, but by the time the query data arrived
  // and triggered a re-render, the prev values would match the current values,
  // causing shouldSkip to become true and queryInput to become skipToken. With
  // skipToken, useQuery returns data: undefined, so the useEffect that
  // dispatches font/html data to Redux would never fire on first page load.
  const lastFetchedKeyRef = React.useRef<string | null>(null);

  const fetchKey =
    !isNullish(projectId) && !isNullish(draftElementId)
      ? `${projectId}:${draftElementId}:${draftElementType}`
      : null;

  const shouldFetch =
    fetchKey !== null && fetchKey !== lastFetchedKeyRef.current;

  const queryInput =
    shouldFetch && projectId && draftElementId
      ? {
          projectId,
          elementId: draftElementId,
        }
      : skipToken;

  const { data, isPlaceholderData } = elementRouter.getCanvasDocument.useQuery(
    queryInput,
    {
      // NOTE (Ben O., 2026-02-17, USE-2357): Keep the previous query data while
      // the query input transitions to skipToken, so the useEffect below can
      // still dispatch the data to Redux even if queryInput changes to skipToken
      // on the same render cycle that data arrives.
      placeholderData: keepPreviousData,
    },
  );

  React.useEffect(() => {
    // NOTE (Ben O., 2026-02-17, USE-2357): Skip dispatching if data is stale
    // placeholder data from a previous query (e.g. when switching between
    // elements, keepPreviousData holds the old element's data until the new
    // query resolves).
    if (!data || !fetchKey || isPlaceholderData) {
      return;
    }

    // NOTE (Ben O., 2026-02-17, USE-2357): Mark this combination as fetched so
    // we don't re-fetch on subsequent renders with the same values.
    lastFetchedKeyRef.current = fetchKey;

    dispatchCanvasData({ data, dispatch, storeUrl });
  }, [data, dispatch, storeUrl, fetchKey, isPlaceholderData]);
}

function dispatchCanvasData({
  data,
  dispatch,
  storeUrl,
}: {
  data: inferRouterOutputs<ElementRouter>["getCanvasDocument"];
  dispatch: ReturnType<typeof useEditorDispatch>;
  storeUrl: string | null | undefined;
}) {
  if (data.html) {
    modalStore
      .getState()
      .actions.closeModal({ type: "storePasswordRequiredModal" });
    dispatch(
      setCanvasHtml(storeUrl ? replaceCdnUrls(data.html, storeUrl) : data.html),
    );
  }

  if (data.fontUrls) {
    dispatch(setSrcDocFontUrls(data.fontUrls));
  }

  if (data.srcDocFonts) {
    dispatch(setSrcDocFonts(data.srcDocFonts));
  }

  if (data.srcDocRootFont) {
    dispatch(setSrcDocRootFont(data.srcDocRootFont));
  }

  dispatch(setActiveCurrency(data.currencyCode));
  dispatch(
    setMoneyFormats({
      moneyFormat: data.moneyFormat,
      moneyWithCurrencyFormat: data.moneyWithCurrencyFormat,
    }),
  );
  dispatch(setActiveLanguage(data.activeLanguage));
  dispatch(setShopifyUrlRoot(data.activeShopifyUrlRoot));
  dispatch(setOkendoWidgetNamespace(data.okendoNamespace ?? null));

  if (data.message) {
    exhaustiveSwitch({ type: data.message })({
      shopifyStoreClosed: () => {
        modalStore.getState().actions.openModal({
          type: "fullPageErrorModal",
          props: {
            details: {
              header: "Shopify store has been closed",
              message:
                "The connected Shopify store has been closed. You will still be able to make edits to this page, but you won't be able to publish or use product data.",
              messageDetail:
                "If you'd like to remove this Shopify store, you can disconnect it in the Integrations settings for your workspace.",
              isFullPage: true,
              callToAction: {
                type: "navigate",
                location: { type: "integrationHub" },
              },
            },
          },
        });
      },
    });
  }
}
